import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Users, DollarSign, Clock, Plus, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { useAuthStore } from '@/stores/auth-store';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';

export function ListingPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Form state
  const [addressLine, setAddressLine] = useState(user?.location?.addressLine || '');
  const [city, setCity] = useState(user?.location?.city || '');
  const [state, setState] = useState(user?.location?.state || '');
  const [dishes, setDishes] = useState<string[]>([]);
  const [dishInput, setDishInput] = useState('');
  const [description, setDescription] = useState('');
  const [availableSeats, setAvailableSeats] = useState(4);
  const [pricePerSeat, setPricePerSeat] = useState('');
  const [timeWindowHours, setTimeWindowHours] = useState(4);
  
  const addDish = () => {
    if (dishInput.trim() && !dishes.includes(dishInput.trim())) {
      setDishes([...dishes, dishInput.trim()]);
      setDishInput('');
    }
  };
  
  const removeDish = (dish: string) => {
    setDishes(dishes.filter(d => d !== dish));
  };
  
  const handleSubmit = async () => {
    if (!addressLine.trim() || !city.trim() || !state.trim()) {
      setError('Por favor, preencha o endereço');
      return;
    }
    
    if (!pricePerSeat) {
      setError('Por favor, defina um preço por pessoa');
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      const now = new Date();
      const endTime = new Date(now.getTime() + timeWindowHours * 60 * 60 * 1000);
      
      await api.createListing({
        location: {
          addressLine,
          city,
          state,
          latitude: user?.location?.latitude || -23.5505,
          longitude: user?.location?.longitude || -46.6333,
        },
        dishes: dishes.length > 0 ? dishes : undefined,
        description: description || undefined,
        availableSeats,
        pricePerSeat: Math.round(parseFloat(pricePerSeat) * 100), // Convert to centavos
        timeWindow: {
          start: now.toISOString(),
          end: endTime.toISOString(),
        },
      });
      
      navigate('/');
    } catch (err) {
      setError('Erro ao criar listagem. Tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <div className="min-h-screen pb-24">
      {/* Header */}
      <header className="border-b bg-background px-4 py-4">
        <div className="mx-auto max-w-lg">
          <h1 className="font-display text-2xl font-bold">Quero Cozinhar</h1>
          <p className="text-muted-foreground">Compartilhe sua refeição com a comunidade</p>
        </div>
      </header>
      
      <main className="mx-auto max-w-lg px-4 py-6">
        <Card className="border-0 shadow-lg">
          <CardHeader>
            <CardTitle>Nova Listagem</CardTitle>
            <CardDescription>
              Informe os detalhes da refeição que você quer compartilhar
            </CardDescription>
          </CardHeader>
          
          <CardContent className="space-y-6">
            {error && (
              <div className="rounded-lg bg-destructive/10 p-3 text-sm text-destructive">
                {error}
              </div>
            )}
            
            {/* Location */}
            <div className="space-y-4">
              <Label className="flex items-center gap-2 text-base">
                <MapPin className="h-4 w-4" />
                Local
              </Label>
              
              <Input
                value={addressLine}
                onChange={(e) => setAddressLine(e.target.value)}
                placeholder="Endereço"
              />
              
              <div className="grid grid-cols-3 gap-2">
                <Input
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  placeholder="Cidade"
                  className="col-span-2"
                />
                <Input
                  value={state}
                  onChange={(e) => setState(e.target.value.toUpperCase().slice(0, 2))}
                  placeholder="UF"
                  maxLength={2}
                />
              </div>
            </div>
            
            {/* Dishes */}
            <div className="space-y-4">
              <Label className="text-base">O que você vai preparar?</Label>
              <p className="text-sm text-muted-foreground">
                Deixe vazio se estiver aberto a sugestões
              </p>
              
              <div className="flex gap-2">
                <Input
                  value={dishInput}
                  onChange={(e) => setDishInput(e.target.value)}
                  placeholder="Ex: Feijoada, Lasanha..."
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addDish())}
                />
                <Button type="button" variant="outline" onClick={addDish}>
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              
              {dishes.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {dishes.map((dish) => (
                    <span
                      key={dish}
                      className="flex items-center gap-1 rounded-full bg-primary/10 px-3 py-1 text-sm text-primary"
                    >
                      {dish}
                      <button onClick={() => removeDish(dish)}>
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
            
            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="description">Descrição (opcional)</Label>
              <textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Conte mais sobre a refeição..."
                className="flex min-h-[100px] w-full rounded-lg border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              />
            </div>
            
            {/* Seats */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-2 text-base">
                  <Users className="h-4 w-4" />
                  Lugares disponíveis
                </Label>
                <span className="font-semibold">{availableSeats}</span>
              </div>
              <input
                type="range"
                min={1}
                max={20}
                value={availableSeats}
                onChange={(e) => setAvailableSeats(Number(e.target.value))}
                className="w-full accent-primary"
              />
            </div>
            
            {/* Price */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2 text-base">
                <DollarSign className="h-4 w-4" />
                Preço por pessoa
              </Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                  R$
                </span>
                <Input
                  type="number"
                  value={pricePerSeat}
                  onChange={(e) => setPricePerSeat(e.target.value)}
                  placeholder="0,00"
                  className="pl-10"
                  min={0}
                  step={0.01}
                />
              </div>
            </div>
            
            {/* Time window */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-2 text-base">
                  <Clock className="h-4 w-4" />
                  Disponível pelas próximas
                </Label>
                <span className="font-semibold">{timeWindowHours}h</span>
              </div>
              <input
                type="range"
                min={1}
                max={12}
                value={timeWindowHours}
                onChange={(e) => setTimeWindowHours(Number(e.target.value))}
                className="w-full accent-primary"
              />
            </div>
            
            <Button
              onClick={handleSubmit}
              className="w-full"
              size="lg"
              disabled={isLoading}
            >
              {isLoading ? 'Criando...' : 'Publicar Listagem'}
            </Button>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}

